import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/api";

/**
 * Features an organization can be given, as the server names them.
 *
 * These are not the same as the OpenFeature flags in `@/lib/openfeature`: those
 * resolve in the browser against a fixed set of defaults, which cannot express
 * "on for this restaurant group". A tenant rule can only be evaluated where the
 * ruleset lives, so the browser asks (T-1106).
 */
export interface ClientFeatures {
  /** The Orders / KDS board. Off unless an organization runs tickets. */
  kdsOrders: boolean;
}

export type ClientFeatureKey = keyof ClientFeatures;

/** Nothing is on until the server says so — a slow answer must not flash a board on. */
const NONE: ClientFeatures = { kdsOrders: false };

export const featureKeys = {
  all: ["client-features"] as const,
};

export function useFeatures() {
  const query = useQuery({
    queryKey: featureKeys.all,
    queryFn: () => api.get<ClientFeatures>("/client-config/features", { skipToast: true }),
    // Whether a tenant has the orders board is not something that changes during
    // a shift, and every nav render asks.
    staleTime: 5 * 60_000,
  });

  return {
    features: query.data ?? NONE,
    isLoading: query.isLoading,
  };
}

/** One feature, for a caller that only cares about one. */
export function useFeature(feature: ClientFeatureKey) {
  const { features, isLoading } = useFeatures();
  return { enabled: features[feature], isLoading };
}
